import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import {
  BaseView,
  BaseSafeFormView,
  HeaderNavigation,
  AppButton,
} from '@/components'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const WithdrawVerifyCodeScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Get params from state
  const { amount, amountCurrencyCode, by } = location.state || {}
  const isByPhone = by === 'phone'

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return

    const newCodes = [...codes]
    newCodes[index] = value
    setCodes(newCodes)

    // Auto-advance to next field
    if (value && index < CELL_COUNT - 1) {
      const nextInput = document.getElementById(`code-input-${index + 1}`)
      nextInput?.focus()
    }

    // If all fields filled, verify code
    if (newCodes.every((code) => code.length === 1)) {
      const fullCode = newCodes.join('')
      handleCodeVerification(fullCode)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleRedirectToSuccessScreen = () => {
    navigate('/withdraw/success', {
      state: {
        amount: String(amount || 0),
        code: amountCurrencyCode || 'USDT',
        title: t('screens:withdraw.youWithdrew'),
      },
    })
  }

  const handleCodeVerification = async (fullCode: string) => {
    setIsLoading(true)
    setErrorMessage('')

    try {
      // TODO: Add real code verification
      // For now using mock verification
      if (fullCode === '0000') {
        handleRedirectToSuccessScreen()
      } else {
        setErrorMessage(t('api:validationErrors.invalidCode'))
        setCodes(defaultCodes)
      }
    } catch {
      setErrorMessage(t('api:errors.somethingWrong'))
      setCodes(defaultCodes)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = () => {
    // TODO: Implement resend code
  }

  return (
    <BaseSafeFormView>
      <HeaderNavigation title={t('screens:auth.verification')} />

      <BaseView flex={1} px={16} py={24} gap={32}>
        <BaseView gap={16} center>
          <p className="text-xl font-semibold text-white text-center">
            {t('screens:auth.enterVerificationCode')}
          </p>
          <p className="text-sm text-gray-400 text-center max-w-sm">
            {isByPhone
              ? t('screens:auth.weSentCodeToYourPhoneNumber')
              : t('screens:auth.weSentCodeToYourEmail')}
          </p>
        </BaseView>

        {/* Code input fields */}
        <BaseView gap={24}>
          <BaseView row justify="center" gap={12}>
            {codes.map((code, index) => (
              <input
                key={index}
                id={`code-input-${index}`}
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 text-center text-lg font-semibold bg-gray-800 border rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                  errorMessage ? 'border-red-500' : 'border-gray-600'
                }`}
                maxLength={1}
                disabled={isLoading}
              />
            ))}
          </BaseView>

          {/* Error */}
          {errorMessage && (
            <p className="text-sm text-red-500 text-center">{errorMessage}</p>
          )}
        </BaseView>

        {/* Resend button */}
        <BaseView center>
          <button
            onClick={handleResend}
            disabled={isLoading}
            className="text-sm text-blue-400 hover:text-blue-300 disabled:text-gray-500"
          >
            {t('form:actions.resendCode')}
          </button>
        </BaseView>
      </BaseView>
    </BaseSafeFormView>
  )
}
